import type {
  EmailBlockContentV2,
  ResolvedEmailBlock,
} from '@/micros/edit-project/grid-areas/main/campaigns/templates/widgets/block-types';
import { WidgetType } from '@/micros/edit-project/grid-areas/main/campaigns/templates/widgets/widget-registry';
import { FOOTER_CARD_STYLE } from '@/micros/edit-project/grid-areas/main/campaigns/templates/utils/utils';
import { DEFAULT_FOOTER_TEXT } from '@/micros/edit-project/grid-areas/main/campaigns/templates/template-defaults';
import type { TranslateString } from '@/types/translation.types';
import { updateTranslateKey } from './update-translation-key';

const TEXT_BLOCK_TYPES = new Set<WidgetType>([
  WidgetType.Header,
  WidgetType.Paragraph,
  WidgetType.Footer,
  WidgetType.Button,
]);

function resolveTranslateString(value: TranslateString, language: string | undefined, baseLanguage: string): string {
  if (language && language !== baseLanguage) {
    return (value[language] as string | undefined) ?? value.base ?? '';
  }
  return value.base ?? '';
}

export function resolveBlocksForLanguage(
  blocks: EmailBlockContentV2[] | undefined,
  language: string | undefined,
  baseLanguage: string
): ResolvedEmailBlock[] | null {
  if (!blocks?.length) return null;

  return blocks.map((block) => {
    if (!TEXT_BLOCK_TYPES.has(block.type)) {
      return { ...block, props: { ...block.props } };
    }
    const value = (block.props as { value?: TranslateString }).value;
    return {
      ...block,
      props: { ...block.props, value: value ? resolveTranslateString(value, language, baseLanguage) : '' },
    };
  });
}

export function serializeBlocksForLanguage(
  resolved: ResolvedEmailBlock[],
  previous: EmailBlockContentV2[] | undefined,
  language: string | undefined,
  baseLanguage: string
): EmailBlockContentV2[] {
  const previousById = new Map((previous ?? []).map((block) => [block.id, block]));

  return resolved.map((block) => {
    const prev = previousById.get(block.id);
    if (!TEXT_BLOCK_TYPES.has(block.type)) {
      return { ...block, props: block.props };
    }
    const prevValue = (prev?.props as { value?: TranslateString } | undefined)?.value;
    const input = (block.props.value as string | undefined) ?? '';
    // the live block's other props (footer logo, toggles, links) are the source of truth - only
    // `value` needs the previous TranslateString to keep the other languages' copy
    return {
      ...block,
      props: {
        ...block.props,
        value: updateTranslateKey({
          translateString: prevValue ?? '',
          input,
          baseLanguage,
          language: language ?? null,
        }),
      },
    };
  });
}

export function createBlockInstance(type: WidgetType): ResolvedEmailBlock {
  const id = `${type}-${crypto.randomUUID()}`;

  switch (type) {
    case WidgetType.Header:
      return { id, type, props: { value: 'Heading text...' } };
    case WidgetType.Paragraph:
      return { id, type, props: { value: 'Start typing here' } };
    case WidgetType.Footer:
      // a template's own footer gets this look from its preset; an added one carries it on style
      return { id, type, props: { value: DEFAULT_FOOTER_TEXT, src: '' }, style: { ...FOOTER_CARD_STYLE } };
    case WidgetType.Button:
      return { id, type, props: { value: 'Button text' } };
    case WidgetType.Image:
      return { id, type, props: { src: '' }, style: { align: 'center' } };
    case WidgetType.Video:
      return { id, type, props: { src: '' }, style: { align: 'center' } };
    case WidgetType.Logo:
      return { id, type, props: { src: '' } };
    case WidgetType.Spacer:
      return { id, type, props: { height: 24 } };
    case WidgetType.Social:
      return { id, type, props: { links: [] } };
    default:
      return { id, type, props: {} };
  }
}
